import ManualField, {
  ManualFieldOption,
  ManualFieldType,
} from "@/types/documents/documentTemplate/ManualField";
import DocumentTemplate from "@/types/documents/documentTemplate/DocumentTemplate";
import { DocumentBatchUserProfile } from "@/types/documents/documentBatch/DocumentBatch";

export type BatchFieldScope = "COMMON" | "INDIVIDUAL";
export type BatchFieldValue = string | string[];
export type BatchFieldValues = Record<string, BatchFieldValue>;

export interface BatchLogicalField {
  key: string;
  label: string;
  type: ManualFieldType;
  required: boolean;
  scope: BatchFieldScope;
  options: ManualFieldOption[];
  templateNames: string[];
}

export interface BatchTemplate extends DocumentTemplate {
  id: string;
}

const aliasToLogicalKey: Record<string, string> = {
  employee_full_name: "person_full_name",
  receiving_full_name: "person_full_name",
  contractor_full_name: "person_full_name",
  employee_iin: "person_iin",
  receiving_iin: "person_iin",
  contractor_iin: "person_iin",
  job_title: "person_position",
  employee_position: "person_position",
  employee_initials: "person_initials",
  employee_initial_name: "person_initials",
  receiving_initial_name: "person_initials",
  employee_address: "person_address",
  receiving_address: "person_address",
  bank_name: "employee_bank_name",
  id_card_issue_date: "employee_id_card_issue_date",
  id_card_number: "employee_id_card_number",
  vacation_days: "leave_days",
  vacation_start_date: "leave_start_date",
  vacation_end_date: "leave_end_date",
  hire_day: "work_start_date",
  hire_month: "work_start_date",
  hire_year: "work_start_date",
  leave_start_day: "leave_start_date",
  leave_start_month: "leave_start_date",
  leave_start_year: "leave_start_date",
  leave_end_day: "leave_end_date",
  leave_end_month: "leave_end_date",
  leave_end_year: "leave_end_date",
  dismissal_day: "dismissal_date",
  dismissal_month: "dismissal_date",
  dismissal_year: "dismissal_date",
  contract_day: "contract_date",
  contract_month: "contract_date",
  contract_year: "contract_date",
  agreement_day: "agreement_date",
  agreement_month: "agreement_date",
  agreement_year: "agreement_date",
  ack_day: "acknowledgement_date",
  ack_month: "acknowledgement_date",
  ack_year: "acknowledgement_date",
};

const individualByDefault = new Set([
  "person_full_name",
  "person_iin",
  "person_position",
  "person_initials",
  "person_address",
  "agreement_end_date",
  "agreement_start_date",
  "application_date",
  "bank_name",
  "compensation_days",
  "compensation_period_end_date",
  "compensation_period_start_date",
  "consent_effective_date",
  "contract_date_en",
  "contract_effective_date",
  "contract_end_day",
  "contract_end_month",
  "contract_number_en",
  "contract_start_day",
  "contract_start_month",
  "contractor_full_name_en",
  "contractor_iin_en",
  "deduction_basic",
  "deduction_opv",
  "deduction_osms",
  "deduction_social",
  "employee_id_card_number",
  "employee_id_card_issue_date",
  "work_start_date",
  "agreement_date",
  "leave_start_date",
  "leave_end_date",
  "dismissal_date",
  "contract_date",
  "acknowledgement_date",
  "contract_end_date",
  "probation_start_date",
  "probation_end_date",
  "salary_amount",
  "salary_amount_words",
  "employee_iban",
  "employee_bank_name",
  "employee_bic",
  "executor_account",
  "executor_address",
  "executor_bank",
  "executor_bik",
  "executor_iin",
  "executor_name",
  "executor_reg_number",
  "executor_representative",
  "executor_sign_name",
  "id_card_issue_date",
  "id_card_issuer",
  "id_card_number",
  "leave_days",
  "passport_issue_date",
  "passport_issue_date_en",
  "passport_issuer",
  "passport_issuer_en",
  "passport_number",
  "passport_number_en",
  "registration_address",
  "residence_address",
  "service_cost",
  "service_cost_words",
  "spouse_application_date",
  "spouse_full_name",
  "vacation_days",
  "vacation_end_date",
  "vacation_start_date",
  "work_period_end_date",
  "work_period_start_date",
  "document_received_date",
  "contract_number",
]);

const dateLogicalKeys = new Set([
  "work_start_date",
  "agreement_date",
  "acknowledgement_date",
  "leave_start_date",
  "leave_end_date",
  "dismissal_date",
  "contract_date",
]);

const ruMonths = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];

const enMonths = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const normalizeTemplateTag = (tag: string): string =>
  String(tag || "")
    .trim()
    .replace(/^key_/, "")
    .replace(/^\{\{\s*/, "")
    .replace(/\s*\}\}$/, "")
    .trim();

export const logicalKeyForTag = (tag: string): string => {
  const normalized = normalizeTemplateTag(tag);
  return aliasToLogicalKey[normalized] || normalized;
};

export const createLogicalFields = (
  templates: BatchTemplate[]
): BatchLogicalField[] => {
  const fields = new Map<string, BatchLogicalField>();
  templates.forEach((template) => {
    (template.manualFields || []).forEach((manualField) => {
      const key = logicalKeyForTag(manualField.tag);
      const existing = fields.get(key);
      const type = dateLogicalKeys.has(key) ? "DATE" : manualField.type;
      if (!existing) {
        fields.set(key, {
          key,
          label: manualField.name || key,
          type,
          required: Boolean(manualField.required),
          scope: individualByDefault.has(key) ? "INDIVIDUAL" : "COMMON",
          options: manualField.options || [],
          templateNames: [template.name],
        });
        return;
      }
      existing.required = existing.required || Boolean(manualField.required);
      if (!existing.templateNames.includes(template.name)) {
        existing.templateNames.push(template.name);
      }
      (manualField.options || []).forEach((option) => {
        if (!existing.options.some((item) => item.value === option.value)) {
          existing.options.push(option);
        }
      });
    });
  });
  return Array.from(fields.values());
};

export const buildProfileValues = (
  profile: DocumentBatchUserProfile
): BatchFieldValues => ({
  person_full_name: profile.fullName || "",
  person_iin: profile.iin || "",
  person_position: profile.jobName || "",
  person_initials: abbreviateFullName(profile.fullName || ""),
  person_address: "",
});

export const abbreviateFullName = (fullName: string): string => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) {
    return fullName.trim();
  }
  const initials = parts
    .slice(1)
    .map((part) => `${part.charAt(0).toUpperCase()}.`)
    .join("");
  return `${parts[0]} ${initials}`;
};

const dateParts = (value: unknown, locale: string) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }
  const [, year, month, day] = match;
  const monthIndex = Number(month) - 1;
  return {
    year,
    month: (locale || "ru").startsWith("en")
      ? enMonths[monthIndex]
      : ruMonths[monthIndex],
    day,
  };
};

export const resolveManualFieldValue = (
  field: ManualField,
  commonValues: BatchFieldValues,
  individualValues: BatchFieldValues,
  scopes: Record<string, BatchFieldScope>
): BatchFieldValue => {
  const normalizedTag = normalizeTemplateTag(field.tag);
  const logicalKey = logicalKeyForTag(field.tag);
  const values =
    scopes[logicalKey] === "COMMON" ? commonValues : individualValues;
  const value = values[logicalKey];
  const parts = dateParts(value, normalizedTag.endsWith("_en") ? "en" : "ru");
  if (parts) {
    if (normalizedTag.endsWith("_day")) return parts.day;
    if (normalizedTag.endsWith("_month")) return parts.month;
    if (normalizedTag.endsWith("_year")) return parts.year;
  }
  return value == null ? "" : value;
};

export const buildTemplateFieldConfig = (
  template: BatchTemplate,
  commonValues: BatchFieldValues,
  individualValues: BatchFieldValues,
  scopes: Record<string, BatchFieldScope>
): Array<{
  key: string;
  name: string;
  type: ManualFieldType;
  required: boolean;
  delimiter?: string | null;
  outputFormat?: string | null;
  options: ManualFieldOption[];
  value: string;
  values?: string[];
}> =>
  (template.manualFields || []).map((field) => {
    const value = resolveManualFieldValue(
      field,
      commonValues,
      individualValues,
      scopes
    );
    return {
      key: field.tag,
      name: field.name,
      type: field.type,
      required: field.required,
      delimiter: field.delimiter,
      outputFormat: field.outputFormat,
      options: field.options || [],
      value: Array.isArray(value)
        ? value.join(field.delimiter || ", ")
        : String(value),
      values: Array.isArray(value) ? value : undefined,
    };
  });

export const isEmptyBatchValue = (value: unknown): boolean =>
  Array.isArray(value)
    ? value.length === 0
    : value == null || String(value).trim() === "";

export const formatTemplateFieldValue = (
  value: string,
  type: ManualFieldType,
  outputFormat?: string | null
): string => {
  if (type !== "DATE" && type !== "DATETIME") {
    return value;
  }

  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (!match) {
    return value;
  }

  const [, year, month, day, hour = "00", minute = "00", second = "00"] = match;
  const format =
    outputFormat?.trim() ||
    (type === "DATETIME" ? "dd.MM.yyyy HH:mm" : "dd.MM.yyyy");
  const tokens: Record<string, string> = {
    yyyy: year,
    yy: year.slice(-2),
    MM: month,
    M: String(Number(month)),
    dd: day,
    d: String(Number(day)),
    HH: hour,
    H: String(Number(hour)),
    mm: minute,
    m: String(Number(minute)),
    ss: second,
    s: String(Number(second)),
  };

  return format.replace(/''|'(?:[^']|'')*'|y+|M+|d+|H+|m+|s+/g, (token) => {
    if (token === "''") {
      return "'";
    }
    if (token.startsWith("'") && token.endsWith("'")) {
      return token.slice(1, -1).replace(/''/g, "'");
    }
    return tokens[token] ?? token;
  });
};

export const validateTemplateValues = (
  template: BatchTemplate,
  commonValues: BatchFieldValues,
  individualValues: BatchFieldValues,
  scopes: Record<string, BatchFieldScope>
): string[] => {
  const errors: string[] = [];
  const config = buildTemplateFieldConfig(
    template,
    commonValues,
    individualValues,
    scopes
  );
  config.forEach((field) => {
    if (field.required && isEmptyBatchValue(field.value)) {
      errors.push(field.name || field.key);
    }
    if (
      (field.type === "SELECT" || field.type === "MULTI_SELECT") &&
      !isEmptyBatchValue(field.value) &&
      field.options.length > 0
    ) {
      const allowed = new Set(field.options.map((option) => option.value));
      const values = field.values || [field.value];
      if (values.some((value) => !allowed.has(value))) {
        errors.push(field.name || field.key);
      }
    }
  });
  return errors;
};

export const findUnresolvedTemplateTags = (
  template: BatchTemplate,
  fieldConfig: Array<{ key: string; value: string }>
): string[] => {
  let html = template.template || "";
  fieldConfig.forEach((field) => {
    if (!field.key) return;
    html = html.split(field.key).join(field.value || "");
    if (!field.key.startsWith("key_")) {
      html = html.split(`key_${field.key}`).join(field.value || "");
    }
  });
  return Array.from(
    new Set(
      Array.from(html.matchAll(/\{\{\s*([^{}]+?)\s*\}\}/g)).map((match) =>
        match[1].trim()
      )
    )
  );
};

export const resolveDocumentTitle = (
  pattern: string,
  templateName: string,
  profileValues: BatchFieldValues
): string =>
  pattern
    .replace(/\{\{\s*template_name\s*\}\}/gi, templateName)
    .replace(
      /\{\{\s*full_name\s*\}\}/gi,
      String(profileValues.person_full_name || "")
    )
    .replace(/\{\{\s*iin\s*\}\}/gi, String(profileValues.person_iin || ""))
    .trim();
